import { KameleoonLogger } from 'src/logging/kameleoonLogger';
import { REAL_TIME_EVENT } from './constants';
import {
  EventHandlerType,
  EventSourceParametersType,
  IExternalEventSource,
  RealTimeEventType,
} from './types';

export interface IEventSource {
  readonly isOpen: boolean;
  open: (onEvent: EventHandlerType) => void;
  close: () => void;
  onError: (callback: (error: Event) => void) => void;
}

// --- Note ---
// Named `KameleoonEventSource` to avoid collision with native `EventSource`
export class KameleoonEventSource implements IEventSource {
  private eventSource: IExternalEventSource;
  private siteCode: string;
  private url: string;
  private isConnectionOpen: boolean;

  constructor({
    siteCode,
    externalEventSource,
    urlProvider,
  }: EventSourceParametersType) {
    this.url = urlProvider.getEventSourceUrl();
    this.siteCode = siteCode;
    this.eventSource = externalEventSource;
    this.isConnectionOpen = false;
  }

  get isOpen(): boolean {
    return this.isConnectionOpen;
  }

  public open(onEvent: EventHandlerType): void {
    this.eventSource.open({
      onEvent: this.handleMessage(onEvent),
      url: this.url,
      eventType: REAL_TIME_EVENT,
    });
    this.isConnectionOpen = true;
    KameleoonLogger.info`Established a new SSE connection with: ${this.url}`;
  }

  public close(): void {
    this.eventSource.close();
    this.isConnectionOpen = false;
    KameleoonLogger.info`SSE connection: '${this.url}' was successfully closed`;
  }

  public onError(callback: (error: Event) => void): void {
    if (!this.eventSource.onError) {
      KameleoonLogger.warning(
        "EventSource does not have `onError` method provided. Real-time errors won't be handled.",
      );
      return;
    }

    this.eventSource.onError(callback);
  }

  private handleMessage(
    onEvent: EventHandlerType,
  ): (message: MessageEvent) => void {
    const onMessage = (message: MessageEvent): void => {
      KameleoonLogger.debug`Received new message from: ${this.url}`;
      const event: RealTimeEventType = JSON.parse(message.data);
      const { siteCode: eventSiteCode, ts } = event;

      if (this.siteCode === eventSiteCode) {
        onEvent(ts);
      }
    };

    return onMessage;
  }
}
